/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import { Component, ErrorInfo, ReactNode } from 'react';

import { LiveAgentEditor } from './codeEditor/LiveAgentEditor';

import { Step } from '@/pages/projects/LiveAgentTypes';

interface Props {
    children: ReactNode;
    steps?: Step[];
}

interface State {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({ error, errorInfo });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="p-4 bg-gradient-to-b from-transparent to-red-100 h-full flex flex-col overflow-scroll rounded">
                    <h2 className="text-lg font-bold mb-2">Something went wrong.</h2>
                    {this.state.error && (
                        <details className="mb-2">
                            <summary className="cursor-pointer font-semibold">Error Details</summary>
                            <pre className="mt-1 p-2 bg-red-50 rounded text-sm overflow-auto">
                                {this.state.error.toString()}
                            </pre>
                        </details>
                    )}
                    {this.state.errorInfo && (
                        <details className="mb-2">
                            <summary className="cursor-pointer font-semibold">Stack Trace</summary>
                            <pre className="mt-1 p-2 bg-red-50 rounded text-sm overflow-auto">
                                {this.state.errorInfo.componentStack}
                            </pre>
                        </details>
                    )}
                    {this.props.steps && (
                        <div className="mt-4 h-full">
                            <h3 className="text-md font-semibold mb-1">Steps Content:</h3>
                            <LiveAgentEditor
                                steps={this.props.steps}
                                dispatch={() => {}}
                                variables={{}}
                                className="overflow-y-auto rounded-[10px] h-full scrollbar-hide"
                                readOnly
                            />
                        </div>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
